import { useCallback, useState } from 'react';
import type { FieldChangeEvent } from '../../types';

export type FormFields = Record<string, unknown>;

const parsedValue = (value: unknown, type?: string | null) => {
  switch (type) {
    case 'string':
      return String(value);
    case 'number':
      return parseInt(String(value), 10);
    case 'boolean':
      return value === 'true';
    default:
      return value;
  }
};

// Multi-value checkbox groups (`name="foo[]"`) accumulate into an array field.
const asArray = (value: unknown): unknown[] =>
  Array.isArray(value) ? value : [];

// Reads a field whose name is only known at runtime (it comes off the event's
// `name`), which a declared interface has no index signature for.
const fieldValue = (fields: object, name: string): unknown =>
  (fields as FormFields)[name];

// Generic over the field shape so a caller that hands in a typed object keeps
// those types on `fields`. `F extends object` rather than `FormFields` because
// interfaces have no implicit index signature. The `as F` casts below are needed
// for the same reason the lookup above is: the written field is dynamic, so
// TypeScript types the spread as `F & {...}` rather than `F`.
const useForm = <F extends object = FormFields>(initialFields: F) => {
  const [fields, updateField] = useState<F>(initialFields);

  const resetField = useCallback((field: string, value: unknown) => {
    updateField(
      prev =>
        ({
          ...prev,
          [field]:
            typeof value === 'function'
              ? value(fieldValue(prev, field))
              : value,
        }) as F
    );
  }, []);

  const resetFields = useCallback(
    (newFields: Partial<F> | ((prev: F) => Partial<F>)) => {
      updateField(
        prev =>
          ({
            ...prev,
            ...(typeof newFields === 'function' ? newFields(prev) : newFields),
          }) as F
      );
    },
    []
  );

  const handleChange = useCallback((event: FieldChangeEvent<unknown>) => {
    const { name, type, checked, dataset } = event.currentTarget;
    const value = parsedValue(
      event.currentTarget.value,
      dataset ? dataset.type : null
    );
    const sanitizedName = name.replace('[]', '');

    switch (type) {
      case 'radio': {
        updateField(prev => ({ ...prev, [name]: value }) as F);

        break;
      }

      case 'checkbox': {
        if (name !== sanitizedName) {
          if (checked) {
            updateField(
              prev =>
                ({
                  ...prev,
                  [sanitizedName]: [
                    ...asArray(fieldValue(prev, sanitizedName)),
                    value,
                  ],
                }) as F
            );
          } else {
            updateField(
              prev =>
                ({
                  ...prev,
                  [sanitizedName]: asArray(
                    fieldValue(prev, sanitizedName)
                  ).filter(item => String(item) !== String(value)),
                }) as F
            );
          }
        } else {
          // eslint-disable-next-line no-lonely-if
          if (checked) {
            updateField(prev => ({ ...prev, [name]: value }) as F);
          } else {
            updateField(prev => ({ ...prev, [name]: '' }) as F);
          }
        }

        break;
      }

      default: {
        updateField(prev => ({ ...prev, [name]: value }) as F);
      }
    }
  }, []);

  return {
    handleChange,
    resetField,
    resetFields,
    fields,
  };
};

export default useForm;
